import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingBag, ExternalLink, Search, Filter, Eye, Package, RefreshCw, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
const API = `${BACKEND_URL}/api`;

const SalesOrdersPage = () => {
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [selectedSO, setSelectedSO] = useState(null);
  const [selectedSOItems, setSelectedSOItems] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    fetchSalesOrders();
  }, []);

  const fetchSalesOrders = async () => {
    try {
      setLoading(true);
      // Use axios to ensure auth token is sent (AuthContext sets Authorization header)
      const response = await axios.get(`${API}/sales-orders`);
      setSalesOrders(response.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching sales orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "pending fulfillment":
        return "secondary";
      case "billed":
        return "default";
      case "pending approval":
        return "outline";
      case "closed":
        return "secondary";
      case "cancelled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    
    // Simple check to see if date is valid
    if (isNaN(date.getTime())) return dateString;

    // Fix timezone offset for YYYY-MM-DD strings to prevent off-by-one error (displaying previous day)
    // We add the user's timezone offset to force it to display as the literal date provided
    if (dateString.length === 10) {
       const userTimezoneOffset = date.getTimezoneOffset() * 60000;
       return new Date(date.getTime() + userTimezoneOffset).toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'short',
         day: 'numeric'
       });
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredOrders = salesOrders.filter(order => {
    const matchesSearch = !searchTerm ||
      (order.order_number?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (order.customer_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesStatus = !statusFilter || statusFilter === "all" || order.status === statusFilter;
    const matchesCustomer = !customerFilter || customerFilter === "all" || order.customer_name === customerFilter;

    return matchesSearch && matchesStatus && matchesCustomer;
  });

  const uniqueStatuses = [...new Set(salesOrders.map(order => order.status).filter(Boolean))];
  const uniqueCustomers = [...new Set(salesOrders.map(order => order.customer_name).filter(Boolean))];

  const handleSyncSalesOrders = async () => {
    setSyncLoading(true);
    try {
      const response = await fetch(`${API}/sync/sales-orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        setTimeout(fetchSalesOrders, 2000); 
      } else {
        console.error("Sync failed:", response.statusText);
      }
    } catch (error) {
      console.error("Error syncing sales orders:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sales Orders</h1>
          <p className="text-gray-600">Manage customer sales orders and shipments</p>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading sales orders...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sales Orders</h1>
          <p className="text-gray-600">Manage customer sales orders and shipments</p>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-12 text-red-600">
              <p className="text-lg font-medium mb-2">Error loading sales orders</p>
              <p className="text-sm">{error}</p>
              <Button onClick={fetchSalesOrders} className="mt-4">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Sales Orders</h1>
        <p className="text-gray-600">Manage customer sales orders and shipments</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <Input
                placeholder="Search SO number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>

            <div className="flex flex-wrap gap-3 items-center lg:flex-shrink-0">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {uniqueStatuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <Select value={customerFilter} onValueChange={setCustomerFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by customer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    {uniqueCustomers.map(customer => (
                      <SelectItem key={customer} value={customer}>{customer}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("");
                  setCustomerFilter("");
                }}
                variant="outline"
                size="sm"
                className="whitespace-nowrap"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sales Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Sales Orders ({filteredOrders.length})
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSyncSalesOrders}
                disabled={syncLoading}
                size="sm"
                variant="outline"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncLoading ? 'animate-spin' : ''}`} />
                {syncLoading ? 'Syncing...' : 'Sync SOs'}
              </Button>
              <Button onClick={fetchSalesOrders} size="sm">
                Refresh
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">No sales orders found</p>
              <p className="text-sm">
                {salesOrders.length === 0
                  ? "No sales orders have been synced yet."
                  : "Try adjusting your filters."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* UPDATED COLUMN ORDER AND HEADERS */}
                    <TableHead className="w-32">SO Number</TableHead>
                    <TableHead className="w-24">Order Date</TableHead>
                    <TableHead className="w-48">Customer</TableHead>
                    <TableHead className="w-48">Ship To</TableHead>
                    <TableHead className="w-32">Status</TableHead>
                    <TableHead className="w-24">Due Date</TableHead>
                    <TableHead className="w-24 text-right">Total</TableHead>
                    <TableHead className="w-24 text-center">Item Count</TableHead>
                    <TableHead className="w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow 
                      key={order.order_number}
                      onClick={() => setSelectedSO(order)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <TableCell className="font-medium">{order.order_number}</TableCell>
                      <TableCell>{formatDate(order.order_date)}</TableCell>
                      <TableCell className="max-w-48 truncate" title={order.customer_name}>
                        {order.customer_name}
                      </TableCell>
                      {/* Shipping Address Summary (Line 1) */}
                      <TableCell className="max-w-48 truncate" title={order.shipping_address_summary}>
                        {order.shipping_address_summary || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(order.status)}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(order.due_date)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(order.total)}
                      </TableCell>
                      {/* Item Count */}
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0 h-auto font-normal text-blue-600 hover:text-blue-800"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent opening the full detail modal
                            setSelectedSOItems(order);
                          }}
                        >
                          <Package className="w-4 h-4 mr-1" />
                          {order.item_count || order.items?.length || 0}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {/* Eye icon - Redundant with row click but requested to keep */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSO(order);
                            }}
                            title="View full details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {order.netsuite_link && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(order.netsuite_link, '_blank');
                              }}
                              title="Open in NetSuite"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full Sales Order Details Modal */}
      <Dialog open={!!selectedSO} onOpenChange={() => setSelectedSO(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Sales Order Details - {selectedSO?.order_number}
            </DialogTitle>
          </DialogHeader>
          {selectedSO && (
            <div className="space-y-6">
              {/* SO Header Information */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="col-span-1">
                  <label className="text-sm font-medium text-gray-500">SO Number</label>
                  <p className="text-lg font-semibold">{selectedSO.order_number}</p>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium text-gray-500">Customer</label>
                  <p className="text-lg font-medium">{selectedSO.customer_name}</p>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <Badge variant={getStatusBadgeVariant(selectedSO.status)}>
                      {selectedSO.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium text-gray-500">Total Amount</label>
                  <p className="text-lg font-semibold text-green-600">{formatCurrency(selectedSO.total)}</p>
                </div>
                
                <div className="col-span-1">
                  <label className="text-sm font-medium text-gray-500">Order Date</label>
                  <p>{formatDate(selectedSO.order_date)}</p>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium text-gray-500">Due Date</label>
                  <p>{formatDate(selectedSO.due_date)}</p>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium text-gray-500">Ship Date</label>
                  <p>{formatDate(selectedSO.ship_date)}</p>
                </div>
                {/* Full Address Block */}
                <div className="col-span-2 lg:col-span-4 bg-gray-50 p-3 rounded-md">
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center border rounded-full text-[10px]">📍</span>
                    Shipping Address
                  </label>
                  <p className="whitespace-pre-line mt-1">
                    {selectedSO.shipping_address_full || selectedSO.shipping_address || selectedSO.shipping_address_summary || "N/A"}
                  </p>
                </div>
              </div>

              {/* NetSuite Link */}
              {selectedSO.netsuite_link && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(selectedSO.netsuite_link, '_blank')}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in NetSuite
                  </Button>
                </div>
              )}

              {/* Items Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Items ({selectedSO.items?.length || 0})
                </h3>
                {selectedSO.items && selectedSO.items.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-64">SKU</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-24 text-right">Qty Ord</TableHead>
                          <TableHead className="w-24 text-right">Qty Rem</TableHead>
                          <TableHead className="w-32">Expected Date</TableHead>
                          <TableHead className="w-32 text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedSO.items.map((item, index) => (
                          <TableRow key={`${selectedSO.order_number}-item-${index}`}>
                            <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                            <TableCell className="max-w-xs">{item.description}</TableCell>
                            <TableCell className="text-right text-gray-500">
                              {item.quantity_ordered?.toLocaleString() || 0}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {item.quantity_remaining?.toLocaleString() || 0}
                            </TableCell>
                            <TableCell>{formatDate(item.expected_date)}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(item.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No items found in this sales order.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Items Details Modal (Quick View - Kept for specific item count click) */}
      <Dialog open={!!selectedSOItems} onOpenChange={() => setSelectedSOItems(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Items for SO {selectedSOItems?.order_number}
            </DialogTitle>
          </DialogHeader>
          {selectedSOItems && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                 <div>
                    <p className="font-semibold">{selectedSOItems.customer_name}</p>
                    <p className="text-sm text-gray-500">{formatDate(selectedSOItems.order_date)}</p>
                 </div>
                 <div className="text-right">
                    <p className="font-semibold text-green-600">{formatCurrency(selectedSOItems.total)}</p>
                    <Badge variant={getStatusBadgeVariant(selectedSOItems.status)} className="text-xs mt-1">
                      {selectedSOItems.status}
                    </Badge>
                 </div>
              </div>

              {selectedSOItems.items && selectedSOItems.items.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-64">SKU</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-24 text-right">Qty Rem</TableHead>
                        <TableHead className="w-32">Expected Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedSOItems.items.map((item, index) => (
                        <TableRow key={`${selectedSOItems.order_number}-item-${index}`}>
                          <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                          <TableCell className="max-w-md">{item.description}</TableCell>
                          <TableCell className="text-right font-medium">
                            {item.quantity_remaining?.toLocaleString() || 0}
                          </TableCell>
                          <TableCell>{formatDate(item.expected_date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No items found.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesOrdersPage;